import {Floorplan} from "../../../api/apiClient";
import {useState} from "react";
import {Box, CircularProgress, Stack} from "@mui/material";
import {BrowserFloorMap} from "./BrowserFloorMap";
import {FloorTabs} from "./FloorTabs";
import {LabeledSwitch} from "./LabeledSwitch";
import {PANEL_GUTTER, SWITCH_ROW_PADDING_PX} from "./useNavLayout";


// Stops a tall, portrait floorplan from squeezing the room list out of the panel.
const MAX_PLAN_HEIGHT_PX = 320;

/** Drawn over the plan while its image loads, and the plan is dimmed behind it. */
const LOADING_PLAN_OPACITY = 0.5;
const LOADING_SPINNER_Z_INDEX = 30;

export interface BrowserMinimapProps {
    floorPlans: Floorplan[];
    currentPanorama: string;
    changePanorama: (panorama: string) => void;
    showViewpoints: boolean;
    setShowViewpoints: (showViewpoints: boolean) => void;
    /** Whether the plan is sharing its height with the room list. */
    capPlanHeight: boolean;
}

export function BrowserMinimap(props: BrowserMinimapProps) {
    const {currentPanorama, changePanorama, floorPlans, showViewpoints, setShowViewpoints,
        capPlanHeight} = props;

    /** The floor the current panorama stands on, or -1 where no floor claims it. */
    function findCurrentFloorIndex() {
        return floorPlans.findIndex((floorPlan) =>
            floorPlan.hotspots.some((hotSpot) => hotSpot.targetPanorama === currentPanorama))
    }

    // No floor claims the panorama when it is only in the room list, and -1 would blank the section.
    const [selectedFloorIndex, setSelectedFloorIndex] = useState(() => Math.max(0, findCurrentFloorIndex()));
    const [prevPanorama, setPrevPanorama] = useState("");
    const [isFloorPlanImageLoading, setIsFloorPlanImageLoading] = useState(true);

    // Handle floor plan change when user selects a floor tab
    const handleFloorPlanChange = (newFloorPlanIndex: number) => {
        setSelectedFloorIndex(newFloorPlanIndex);
        setIsFloorPlanImageLoading(true);

        // Change panorama to the default viewpoint of the new floor plan.
        const selectedFloorPlan = floorPlans[newFloorPlanIndex];
        if (selectedFloorPlan.defaultViewpoint) {
            changePanorama(selectedFloorPlan.defaultViewpoint);
        }
    }

    // Travelling to another floor from anywhere other than the tabs still moves the tabs with it.
    if (currentPanorama !== prevPanorama) {
        const foundFloorPlanIndex = findCurrentFloorIndex();
        if (foundFloorPlanIndex >= 0 && selectedFloorIndex !== foundFloorPlanIndex) {
            setSelectedFloorIndex(foundFloorPlanIndex);
            setIsFloorPlanImageLoading(true);
        }

        setPrevPanorama(currentPanorama);
    }

    const currentFloorPlan = floorPlans[selectedFloorIndex];

    if (!currentFloorPlan) {
        return <></>;
    }

    return <Stack sx={{flexGrow: 1, minHeight: 0}}>

        {/*Floor selection*/}
        <FloorTabs floorPlans={floorPlans} selectedFloorIndex={selectedFloorIndex}
                   onSelectFloor={handleFloorPlanChange}/>

        {/*Floorplan image. Deliberately unframed: a border would outline the leftover space around the
           plan rather than the plan itself.*/}
        <Box sx={{
            position: "relative",
            flex: "0 1 auto",
            aspectRatio: `${currentFloorPlan.imageWidth} / ${currentFloorPlan.imageHeight}`,
            maxHeight: capPlanHeight ? MAX_PLAN_HEIGHT_PX : undefined,
            minHeight: 0,
            mx: PANEL_GUTTER,
            display: "flex",
            opacity: isFloorPlanImageLoading ? LOADING_PLAN_OPACITY : 1
        }}>
            <BrowserFloorMap changePanorama={changePanorama} floorPlan={currentFloorPlan}
                             currentPanorama={currentPanorama} showViewpoints={showViewpoints}
                             onFloorPlanImageLoaded={() => setIsFloorPlanImageLoading(false)}/>

            {/*Loading symbol, centred on the plan by its own size.*/}
            {isFloorPlanImageLoading && <CircularProgress
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: LOADING_SPINNER_Z_INDEX
                }}/>}
        </Box>

        {/*Viewpoint visibility*/}
        <Box sx={{flexShrink: 0, px: PANEL_GUTTER, py: `${SWITCH_ROW_PADDING_PX}px`}}>
            <LabeledSwitch label={"Show viewpoints"} checked={showViewpoints} setChecked={setShowViewpoints}/>
        </Box>
    </Stack>
}
